<template>
  <div class="service-records-documentation">
    <BaseTab
      v-model="activeTab"
      :tabs="tabs"
      :unmount-inactive="false"
      @update:model-value="handleTabChange"
    >
      <template #dokumenter>
        <DocumentationTabContent
          :service-offer-id="serviceOfferId"
          :supplier-request-id="supplierRequestId"
          :tender-id="tender.tenderId"
          :customer-org-number="tender.customerOrganization.orgNumber"
          :supplier-id="supplierId"
          :accepted-service-offer="acceptedServiceOffer"
          :local-documentation="localDocumentation"
          @documentation-created="handleDocumentationCreated"
        />
      </template>

      <template #service>
        <ServicesTabContent
          :show-form="showServicesForm"
          :form-props="servicesFormProps"
          @toggle-form="toggleServicesForm"
          @hide-form="hideServicesForm"
          @new-service-record="handleNewServiceRecord"
          @update-service-record="handleUpdateServiceRecord"
        />
      </template>
    </BaseTab>
  </div>
</template>

<script setup lang="ts">
import { ref, watch, computed } from "vue";
import BaseTab from "@/components/Base/BaseTab/BaseTab.vue";
import type { TabItem } from "@/components/Base/BaseTab/BaseTab.vue";
import type {
  ServiceRecord,
  DocumentationRecord,
} from "@/types/service-records";
import type { ServiceOffer } from "@/custom-types/serviceOffers";

import DocumentationTabContent from "./components/DocumentationTabContent.vue";
import ServicesTabContent from "./components/ServicesTabContent.vue";

interface Props {
  tender: {
    tenderId: number;
    customerOrganization: {
      orgNumber: string;
    };
  };
  supplierRequestId: number;
  serviceOfferId: number;
  supplierId: number;
  editingRecord?: ServiceRecord | null;
  acceptedServiceOffer?: ServiceOffer | null;
  localDocumentation?: DocumentationRecord | null;
}

interface Emits {
  (e: "new-service-record"): void;
  (e: "active-tab-changed", tabValue: string): void;
  (e: "documentation-submitted", documentation: DocumentationRecord): void;
}

const props = defineProps<Props>();
const emit = defineEmits<Emits>();

// Reactive state
const activeTab = ref("dokumenter");
const showServicesForm = ref(false);

// Computed properties following the Extract Logic pattern
const tabs: TabItem[] = [
  {
    label: "Dokumentasjon",
    value: "dokumenter",
  },
  {
    label: "Servicerapport",
    value: "service",
  },
];

const servicesFormProps = computed(() => ({
  serviceOfferId: props.serviceOfferId,
  tenderId: props.tender.tenderId,
  customerOrgNumber: props.tender.customerOrganization.orgNumber,
  supplierId: props.supplierId,
  editingRecord: props.editingRecord,
}));

// Watchers
watch(
  () => props.editingRecord,
  (newEditingRecord, oldEditingRecord) => {
    if (newEditingRecord) {
      activeTab.value = "service";
      showServicesForm.value = true;
      scrollToServicesForm();
    } else if (oldEditingRecord && !newEditingRecord) {
      showServicesForm.value = false;
    }
  },
  { immediate: true, deep: true },
);

// Event handlers
const handleTabChange = (tabValue: string) => {
  emit("active-tab-changed", tabValue);
};

const handleNewServiceRecord = () => {
  emit("new-service-record");
  showServicesForm.value = false;
};

const handleUpdateServiceRecord = () => {
  emit("new-service-record");
  showServicesForm.value = false;
};

const handleDocumentationCreated = (documentation: DocumentationRecord) => {
  // Documentation was successfully created and stored in the child component

  // Emit the documentation data for immediate display
  emit("documentation-submitted", documentation);

  // Also emit for data refresh as needed
  emit("new-service-record");
};

const toggleServicesForm = () => {
  showServicesForm.value = !showServicesForm.value;
};

const hideServicesForm = () => {
  showServicesForm.value = false;
};

// Helper functions
const scrollToServicesForm = () => {
  setTimeout(() => {
    const servicesForm = document.querySelector(".services-form");
    const tabContent = document.querySelector(
      ".service-records-documentation__tab-content",
    );
    const targetElement = servicesForm || tabContent;

    if (targetElement) {
      targetElement.scrollIntoView({
        behavior: "smooth",
        block: "start",
        inline: "nearest",
      });
    }
  }, 150);
};
</script>

<style lang="scss" scoped>
.service-records-documentation {
  @media (max-width: $bp-sm) {
    padding: 1rem;
  }
}
</style>
